import { exclusionCalendarDate } from './recurrenceExclusions'
import dayjs, { Dayjs } from 'dayjs'
import { Frequency, Options, RRule, RRuleSet } from 'rrule'
import Database from 'services/db/Database'
import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventStatus,
  Exclusion
} from 'types/api/changes'
import { isTrulyEmpty } from 'utils/object'
import { eventEndTimestamp } from './eventDates'
import { dateInTimezone, resolveTimezone } from 'utils/timezone'
import { expandZonedRecurrence } from 'utils/zonedRecurrence'

// Include recurrence data: local date repairs deliberately preserve revisions.
// Lets repeat month navigation skip rrule.between and the per-instance dayjs work.
const RECURRENCE_CACHE_MAX = 500
const recurrenceCache = new Map<string, EventDoc[]>()

function recurrenceCacheKey(
  event: EventDoc,
  ignoreExclusions: boolean,
  fromMs: number,
  toMs: number,
  tz: string
): string | null {
  if (!event._id || event._id.length === 0) return null
  if (event._rev === undefined || event._rev === null) return null
  return `${event._id}|${event._rev}|${ignoreExclusions ? 1 : 0}|${fromMs}|${toMs}|${tz}|${JSON.stringify(event.Recurrence)}`
}

export function generateGroupcalArrayFromRecurrence(
  groupcalEvent: EventDoc,
  ignoreExclusions = false,
  from?: Dayjs,
  to?: Dayjs,
  defaultTz?: string
): EventDoc[] {
  defaultTz = resolveTimezone(defaultTz)

  const today = dayjs.utc(
    dateInTimezone(Date.now(), defaultTz).format('YYYY-MM-DD')
  )
  const instancesFrom =
    from ?? dayjs.tz(today.subtract(1, 'week').format('YYYY-MM-DD'), defaultTz)
  const instancesTo =
    to ?? dayjs.tz(today.add(50, 'day').format('YYYY-MM-DD'), defaultTz)

  if (!groupcalEvent.Recurrence || typeof groupcalEvent.Recurrence === 'string')
    return [groupcalEvent]
  //return itself in
  if (groupcalEvent._id?.length === 0) return [groupcalEvent]

  const cacheKey = recurrenceCacheKey(
    groupcalEvent,
    ignoreExclusions,
    instancesFrom.valueOf(),
    instancesTo.valueOf(),
    defaultTz
  )
  if (cacheKey) {
    const cached = recurrenceCache.get(cacheKey)
    if (cached) {
      recurrenceCache.delete(cacheKey)
      recurrenceCache.set(cacheKey, cached)
      return cached
    }
  }

  try {
    const durInSeconds =
      Number(eventEndTimestamp(groupcalEvent)) - Number(groupcalEvent.StartDate)

    const rruleString = mapRecurrency(
      groupcalEvent,
      true,
      ignoreExclusions,
      defaultTz
    )

    if (isTrulyEmpty(rruleString)) {
      return [groupcalEvent]
    }
    let instances = expandZonedRecurrence(
      rruleString,
      instancesFrom.toDate(),
      instancesTo.toDate()
    )
    const endRecurrence = groupcalEvent.Recurrence?.EndDate
    if (endRecurrence && !isTrulyEmpty(endRecurrence)) {
      const zone =
        groupcalEvent.AllDay === '1'
          ? 'UTC'
          : resolveTimezone(groupcalEvent.TimeZoneNameID, defaultTz)
      const lastDate = dayjs
        .unix(Number(endRecurrence))
        .tz(zone)
        .format('YYYY-MM-DD')
      instances = instances.filter(
        (instance) => dayjs(instance).tz(zone).format('YYYY-MM-DD') <= lastDate
      )
    }

    // Hoist invariants outside the per-instance loop
    const eventDoc = Database.toServerObject(groupcalEvent)
    const baseEvent: EventDoc = { ...groupcalEvent, ...eventDoc }

    const result = instances.map((instance) => {
      const copyEvent: EventDoc = { ...baseEvent }
      copyEvent.StartDate = (instance.valueOf() / 1000).toString()
      copyEvent.EndDate = (instance.valueOf() / 1000 + durInSeconds).toString()
      if (typeof copyEvent.Recurrence?.Exclusion !== 'string') {
        copyEvent.Status =
          eventDoc.Status !== EventStatus.ACTIVE
            ? eventDoc.Status
            : String(
                findStatusForInstance(
                  copyEvent,
                  Number(copyEvent.StartDate) * 1000,
                  defaultTz
                )
              )
      }
      return copyEvent
    })

    if (cacheKey) {
      if (recurrenceCache.size >= RECURRENCE_CACHE_MAX) {
        const oldestKey = recurrenceCache.keys().next().value
        if (oldestKey !== undefined) recurrenceCache.delete(oldestKey)
      }
      recurrenceCache.set(cacheKey, result)
    }

    return result
  } catch (e) {
    console.log(groupcalEvent)
    console.log(instancesFrom)
    console.log(instancesTo)
    console.error(e)
    return [groupcalEvent]
  }
}

export function findStatusForInstance(
  copyEvent: EventDoc,
  instanceTime: number,
  defaultTz?: string
) {
  let maxStatus = Number(EventStatus.ACTIVE)
  const tz =
    copyEvent.AllDay === '1'
      ? 'UTC'
      : resolveTimezone(copyEvent.TimeZoneNameID, resolveTimezone(defaultTz))
  for (const exclusion of copyEvent.Recurrence?.Exclusion ?? []) {
    const exclusionDate = exclusionCalendarDate(exclusion, tz)
    if (exclusionDate === dayjs(instanceTime).tz(tz).format('YYYY-MM-DD')) {
      if (Number(exclusion.Status) > maxStatus)
        maxStatus = Number(exclusion.Status)
    }
  }

  return maxStatus
}

/**
 * converts groupcal recurrence to the rfc5545 rrule, see {@link https://www.rfc-editor.org/rfc/rfc5545#section-3.3.10}
 * {@link https://www.rfc-editor.org/rfc/rfc5545#section-3.8.5}
 * @param event 'groupcal' model of the event
 * @param keepDTStart flag which determines DTSTART field of RRULE
 * @returns string representation of a 'groupcal' recurrence
 */
export function mapRecurrency(
  event: EventDoc,
  keepDTStart: boolean,
  ignoreExclusions = false,
  defaultTz?: string
): string {
  defaultTz = resolveTimezone(defaultTz)
  let options: Partial<Options> | undefined = undefined
  const recurrence: EventRecurrence | undefined = event.Recurrence
  const rruleSet: RRuleSet = new RRuleSet()

  if (recurrence && recurrence.Interval) {
    options = {
      interval: recurrence.Interval
    }

    if (recurrence.Unit === 5) {
      options.byweekday = RRule.FR.nth(recurrence.Interval)
    }

    if (recurrence.Unit) {
      options.freq = groupCalFrequencyToGoogle(Number(recurrence.Unit))
    }

    rruleSet.rrule(new RRule(options))

    let rruleForEvent = rruleSet.toString()

    const eventTz = resolveTimezone(event.TimeZoneNameID, defaultTz)

    let eventStart =
      event.AllDay === '1'
        ? dayjs.unix(Number(event.StartDate)).utc()
        : dateInTimezone(Number(event.StartDate) * 1000, eventTz)

    if (keepDTStart) {
      /**
       * In order to preserve original start time of recurring event between daylight savings, we need to set
       * DTSTART and UNTIL manually, so event will not jump in hours on view
       */
      let validStart = `DTSTART;TZID=${event.AllDay === '1' ? 'UTC' : eventTz}:${eventStart.format(`YYYYMMDDTHHmmss`)}\n\r`

      if (
        (event.Type === DocType.TASK || event.Type === DocType.EVENT) &&
        event.AllDay === '1' &&
        (event.TimeZoneNameID?.length === 0 || event.TimeZoneNameID !== 'UTC')
      ) {
        // Exclusions must use the same wall time as the shifted DTSTART.
        eventStart = eventStart.add(13, 'hour')
        validStart = `DTSTART:${eventStart.format('YYYYMMDDTHHmmss')}\n\r`
      }

      rruleForEvent = rruleForEvent.padStart(
        rruleForEvent.length + validStart.length,
        validStart
      )
    }

    if (recurrence?.EndDate && !isNaN(+recurrence?.EndDate)) {
      const zone = event.AllDay === '1' ? 'UTC' : eventTz
      const endDate = dayjs
        .unix(Number(recurrence.EndDate))
        .tz(zone)
        .format('YYYY-MM-DD')
      const nextDate = dayjs.utc(endDate).add(1, 'day').format('YYYY-MM-DD')
      const endRecur = dayjs.tz(nextDate, zone).subtract(1, 'second').utc()
      const validEnd = `;UNTIL=${endRecur.format('YYYYMMDDTHHmmss')}${event.AllDay === '1' && keepDTStart ? '' : 'Z'}`

      rruleForEvent = rruleForEvent.padEnd(
        rruleForEvent.length + validEnd.length,
        validEnd
      )
    }
    if (
      event?.Recurrence?.Exclusion &&
      Array.isArray(event?.Recurrence?.Exclusion) &&
      !ignoreExclusions
    ) {
      event.Recurrence?.Exclusion?.forEach((excl: Exclusion) => {
        try {
          if (
            excl.Status !== EventStatus.COMPLETED &&
            excl.Status !== EventStatus.ACTIVE
          ) {
            const exclusionDate = exclusionCalendarDate(
              excl,
              event.AllDay === '1' ? 'UTC' : eventTz
            )
            if (!exclusionDate) return
            const wallTime = `${exclusionDate} ${eventStart.format('HH:mm:ss')}`
            const excluded = dayjs.tz(
              wallTime,
              event.AllDay === '1' ? 'UTC' : eventTz
            )
            rruleForEvent = `${rruleForEvent}\n\rEXDATE:${excluded.format('YYYYMMDDTHHmmss')}`
          }
        } catch (e) {
          console.error(e)
        }
      })
    }

    return rruleForEvent
  }

  return rruleSet.toString()
}

export function groupCalFrequencyToGoogle(freq: number): Frequency {
  switch (freq) {
    case 1:
      return Frequency.DAILY
    case 2:
      return Frequency.WEEKLY
    case 3:
      return Frequency.MONTHLY
    case 4:
      return Frequency.YEARLY
  }

  return Frequency.DAILY
}
